import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Disclosure, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Analytics from '../../services/analytics';
import SignupFormModal from '../ui/SignupFormModal';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Features', href: '/features' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Integrations', href: '/integrations' },
];

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const openSignupModal = () => {
    Analytics.trackButtonClick('Get Started', 'header');
    setIsSignupModalOpen(true);
  };

  return (
    <>
      <Disclosure as="nav" className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-1' : 'bg-transparent py-2'}`}>
        {({ open }) => (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-12">
                <div className="flex items-center">
                  <Link to="/" className="flex-shrink-0 flex items-center">
                    <motion.div
                      className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 mr-2"
                      whileHover={{ rotate: 5, scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                    <motion.span 
                      className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-secondary-600"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      AgentSpot
                    </motion.span>
                  </Link>
                  <div className="hidden sm:ml-10 sm:flex sm:space-x-8">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`inline-flex items-center relative px-1 pt-1 text-sm font-medium ${
                            isActive 
                              ? 'text-primary-600'
                              : 'text-gray-500 hover:text-gray-900'
                          }`}
                        >
                          {item.name}
                          {isActive && (
                            <motion.div 
                              className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-500"
                              layoutId="navbar-indicator"
                            />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <div className="hidden sm:flex sm:items-center sm:space-x-4">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <button 
                      className="btn btn-primary py-1 px-3 text-sm min-w-[90px] text-center inline-flex items-center justify-center h-8"
                      onClick={openSignupModal}
                    >
                      Get Started
                    </button>
                  </motion.div>
                </div>
                <div className="flex items-center sm:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="sm:hidden bg-white">
                <div className="pt-2 pb-3 space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                          isActive
                            ? 'border-primary-500 text-primary-700 bg-primary-50'
                            : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
                <div className="pt-4 pb-3 border-t border-gray-200">
                  <div className="flex items-center px-4 space-x-3">
                    <button 
                      className="btn btn-primary w-full justify-center"
                      onClick={() => {
                        Analytics.trackButtonClick('Get Started', 'mobile-header');
                        setIsSignupModalOpen(true);
                      }}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
      
      {/* Signup Modal */}
      <SignupFormModal 
        isOpen={isSignupModalOpen} 
        onClose={() => setIsSignupModalOpen(false)} 
      />
    </>
  );
};

export default Header;